import { useState, useEffect, useCallback } from 'react';
import { clockIn, clockOut, getCurrentTimeEntry, getTimeEntries, getErrorMessage } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Clock, MapPin, AlertTriangle, CheckCircle2, History } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export const ClockPage = () => {
  const [currentEntry, setCurrentEntry] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [currentRes, entriesRes] = await Promise.all([
        getCurrentTimeEntry(),
        getTimeEntries({})
      ]);
      setCurrentEntry(currentRes.data);
      setRecentEntries(entriesRes.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch time entries:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Get location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          setLocationError('Location access denied. Clock-in will still work but may be flagged.');
        }
      );
    }

    return () => clearInterval(timer);
  }, [fetchData]);

  const handleClockIn = async () => {
    setActionLoading(true);
    try {
      const response = await clockIn({
        latitude: location?.latitude,
        longitude: location?.longitude
      });
      setCurrentEntry(response.data);
      
      if (response.data.is_flagged) {
        toast.warning('Clocked in but flagged for review', {
          description: response.data.flag_reason
        });
      } else {
        toast.success('Successfully clocked in!');
      }
      
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to clock in'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      const response = await clockOut({
        latitude: location?.latitude,
        longitude: location?.longitude
      });
      setCurrentEntry(null);
      
      if (response.data.is_flagged) {
        toast.warning('Clocked out but flagged for review', {
          description: response.data.flag_reason
        });
      } else {
        toast.success(`Clocked out! Total: ${response.data.total_hours} hours`);
      }
      
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to clock out'));
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'h:mm a');
    } catch {
      return '-';
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return '-';
    }
  };

  const getElapsedTime = () => {
    if (!currentEntry?.clock_in) return '00:00:00';
    const start = parseISO(currentEntry.clock_in);
    const diff = Math.floor((currentTime - start) / 1000);
    const hours = Math.floor(diff / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((diff % 3600) / 60).toString().padStart(2, '0');
    const seconds = (diff % 60).toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="clock-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="clock-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
          Time Clock
        </h1>
        <p className="text-muted-foreground mt-1">
          Clock in and out to track your working hours
        </p>
      </div>

      {/* Main Clock Widget */}
      <Card className="overflow-hidden">
        <div className="clock-widget">
          <div className="relative z-10 text-center">
            <p className="text-lg opacity-80 mb-2">
              {format(currentTime, 'EEEE, MMMM d, yyyy')}
            </p>
            <p className="clock-time">
              {format(currentTime, 'HH:mm:ss')}
            </p>
            
            {currentEntry ? (
              <>
                <div className="clock-status clocked-in mt-4">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse-soft" />
                  Working since {formatTime(currentEntry.clock_in)}
                </div>
                <p className="text-4xl font-bold mt-6 font-['JetBrains_Mono']">
                  {getElapsedTime()}
                </p>
              </>
            ) : (
              <div className="clock-status clocked-out mt-4">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                Not clocked in
              </div>
            )}

            <div className="mt-8">
              {currentEntry ? (
                <Button
                  onClick={handleClockOut}
                  disabled={actionLoading}
                  size="lg"
                  className="rounded-full px-10 py-6 text-lg bg-white text-slate-900 hover:bg-gray-100"
                  data-testid="clock-out-btn"
                >
                  {actionLoading ? 'Processing...' : 'Clock Out'}
                </Button>
              ) : (
                <Button
                  onClick={handleClockIn}
                  disabled={actionLoading}
                  size="lg"
                  className="rounded-full px-10 py-6 text-lg bg-white text-slate-900 hover:bg-gray-100"
                  data-testid="clock-in-btn"
                >
                  {actionLoading ? 'Processing...' : 'Clock In'}
                </Button>
              )}
            </div>

            {/* Location status */}
            <div className="mt-6 flex items-center justify-center gap-2 text-sm">
              {location ? (
                <>
                  <MapPin className="w-4 h-4" />
                  <span className="opacity-80">Location detected</span>
                </>
              ) : locationError ? (
                <>
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="opacity-80">{locationError}</span>
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4 opacity-50" />
                  <span className="opacity-60">Detecting location...</span>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Recent Time Entries */}
      <Card data-testid="recent-entries-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Manrope']">
            <History className="w-5 h-5" />
            Recent Time Entries
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentEntries.length === 0 ? (
            <div className="empty-state py-8">
              <Clock className="empty-state-icon" />
              <p className="text-muted-foreground">No time entries yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                  data-testid={`time-entry-${entry.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center">
                      {entry.is_flagged ? (
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                      ) : entry.clock_out ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <Clock className="w-5 h-5 text-blue-500" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{formatDate(entry.clock_in)}</p>
                      <p className="text-sm text-muted-foreground">
                        {formatTime(entry.clock_in)} - {entry.clock_out ? formatTime(entry.clock_out) : 'In Progress'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {entry.total_hours && (
                      <p className="font-bold font-['JetBrains_Mono']">
                        {entry.total_hours.toFixed(2)} hrs
                      </p>
                    )}
                    {entry.is_flagged && (
                      <Badge variant="outline" className="badge-warning mt-1">
                        Flagged
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
